import React from 'react';
import { Course, UserRole } from '../../types/lms.types';

interface GlossaryViewProps {
  course: Course;
  userRole: UserRole;
  selectedModule: string;
}

const GlossaryView: React.FC<GlossaryViewProps> = ({ 
  course, 
  userRole, 
  selectedModule 
}) => {
  // Mock glossary data
  const glossaryTerms = [
    {
      id: 1,
      term: 'Linear Equation',
      description: 'An algebraic equation in which each term is either a constant or the product of a constant and a single variable. Linear equations can be written in the form ax + b = c, where a, b, and c are constants.',
      moduleId: 'mod1',
      examples: ['2x + 5 = 11', '3y - 7 = 14'],
      relatedTerms: ['Variable', 'Constant', 'Coefficient']
    },
    {
      id: 2,
      term: 'Variable',
      description: 'A symbol, usually a letter, that represents an unknown number or quantity. Variables are used in mathematical expressions and equations to represent values that can change or that we want to solve for.',
      moduleId: 'mod1',
      examples: ['x in the equation x + 5 = 12', 'y in the expression 3y + 2'],
      relatedTerms: ['Linear Equation', 'Expression']
    },
    {
      id: 3,
      term: 'Coefficient',
      description: 'A numerical factor that multiplies a variable in an algebraic expression. The coefficient tells us how many times the variable is counted.',
      moduleId: 'mod1',
      examples: ['In 5x, the coefficient is 5', 'In -3y, the coefficient is -3'],
      relatedTerms: ['Variable', 'Linear Equation']
    },
    {
      id: 4,
      term: 'Quadratic Equation',
      description: 'A polynomial equation of degree 2, typically written in the form ax² + bx + c = 0, where a ≠ 0. Quadratic equations can have zero, one, or two real solutions.',
      moduleId: 'mod3',
      examples: ['x² + 5x + 6 = 0', '2x² - 7x + 3 = 0'],
      relatedTerms: ['Polynomial', 'Discriminant', 'Quadratic Formula']
    },
    {
      id: 5,
      term: 'System of Equations',
      description: 'A set of two or more equations with the same variables. The solution to a system is the set of values that satisfy all equations simultaneously.',
      moduleId: 'mod2',
      examples: ['x + y = 5 and 2x - y = 1', '3a + 2b = 7 and a - b = 1'],
      relatedTerms: ['Linear Equation', 'Solution Set']
    }
  ].filter(term => selectedModule === 'all' || term.moduleId === selectedModule);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-xl font-semibold">Glossary</h2>
        <div className="text-sm text-gray-500">
          {glossaryTerms.length} terms
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {glossaryTerms.map((term) => (
          <div
            key={term.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📖</span>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {term.term}
                  </h3>
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                {term.description}
              </p>

              {term.examples && term.examples.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Examples:
                  </h4>
                  <ul className="list-disc list-inside space-y-1">
                    {term.examples.map((example, index) => (
                      <li key={index} className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {term.relatedTerms && term.relatedTerms.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Related Terms:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {term.relatedTerms.map((relatedTerm, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                      >
                        {relatedTerm}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {glossaryTerms.length === 0 && (
          <div className="text-center py-12">
            <span className="text-6xl mb-4 block">📖</span>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No glossary terms found
            </h3>
            <p className="text-gray-500">
              No terms available for the selected module.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlossaryView;
